// The learner's own account: the facts they are known by, the language the
// site speaks to them in, and their password.
//
// There is no email on file anywhere in this installation, so there is no
// "forgot my password" to fall back on. That makes the password form the only
// way an account whose password has leaked can be taken back, and it is why the
// notice above it says out loud what pressing the button does to every other
// device.

import { useId, useState } from 'react'
import type { FormEvent } from 'react'

import { ApiError, api } from '../api'
import type { User } from '../api'
import { useAuth } from '../auth'
import { Flag, LANGS, useI18n, useT } from '../i18n'
import auth from '../i18n/auth'
import common from '../i18n/common'
import { clock, lockOf, useLockClock } from '../lib/lock'
import { Icon, formatUzPhone, messageOf } from '../lib/ui'
import { PasswordField } from './SignIn'
import '../styles/auth.css'

// The bounds the server holds (domain.PasswordMinLen and PasswordMaxLen),
// repeated here so a password that is plainly too short is answered without a
// round trip. Deliberately no stricter than the server's.
const PASSWORD_MIN = 8
const PASSWORD_MAX = 200

type Field = 'current_password' | 'new_password' | 'again'
type Errors = Partial<Record<Field, string>>

/**
 * The signed-in learner as /auth/me and /auth/login send them. The server's
 * user carries `username`, which the shared `User` type does not declare; this
 * screen states the one extra field it reads. Empty for an account without one.
 */
type Me = User & { username?: string }

export function Settings() {
  const t = useT(auth)
  const { user } = useAuth()

  // Signed-out callers never reach this screen; App swaps the whole tree for
  // the login page before any of its routes are matched.
  if (!user) return null

  return (
    <div className="settings">
      <h1>{t('settingsTitle')}</h1>
      <Profile fullName={user.full_name} phone={user.phone} username={(user as Me).username ?? ''} />
      <Language />
      <ChangePassword />
    </div>
  )
}

// ----------------------------------------------------------------- profile --

function Profile({ fullName, phone, username }: { fullName: string; phone: string; username: string }) {
  const t = useT(auth)
  const tc = useT(common)
  const id = useId()
  return (
    <section className="card" aria-labelledby={id}>
      <div className="card__body">
        <h2 id={id}>{t('profileTitle')}</h2>
        <dl className="settings__facts">
          <dt>{t('fullNameLabel')}</dt>
          <dd>{fullName || <span className="muted">{tc('notSet')}</span>}</dd>

          <dt>{t('phoneLabel')}</dt>
          <dd className="settings__phone">{readablePhone(phone)}</dd>

          <dt>{t('usernameLabel')}</dt>
          <dd>{username || <span className="muted">{t('usernameNotSet')}</span>}</dd>
        </dl>
        <p className="settings__note">{t('profileReadOnly')}</p>
      </div>
    </section>
  )
}

/** +998901234567 → "+998 90 123 45 67"; any other number as it is stored. */
function readablePhone(phone: string): string {
  return /^\+998\d{9}$/.test(phone) ? `+998 ${formatUzPhone(phone.slice(4))}` : phone
}

// ---------------------------------------------------------------- language --

/**
 * The four languages as a set of radios. Choosing one switches the site at
 * once and saves the choice to the account (the i18n provider sends it), so it
 * follows the learner to their next device and to the staff panel.
 */
function Language() {
  const t = useT(auth)
  const { lang, setLang } = useI18n()
  const id = useId()
  return (
    <section className="card" aria-labelledby={id}>
      <div className="card__body">
        <h2 id={id}>{t('languageTitle')}</h2>
        <fieldset className="lang-choice">
          <legend className="visually-hidden">{t('languageGroup')}</legend>
          {LANGS.map((l) => (
            <label key={l.code} className="lang-choice__option" lang={l.code}>
              <input
                type="radio"
                name={`${id}-lang`}
                checked={lang === l.code}
                onChange={() => setLang(l.code)}
              />
              <Flag code={l.code} />
              <span>{l.label}</span>
              {lang === l.code && (
                <span className="lang-choice__tick">
                  <Icon name="check" size={18} />
                </span>
              )}
            </label>
          ))}
        </fieldset>
        <p className="settings__note">{t('languageHint')}</p>
      </div>
    </section>
  )
}

// ---------------------------------------------------------------- password --

function ChangePassword() {
  const t = useT(auth)
  const tc = useT(common)
  const { signOut } = useAuth()
  const id = useId()

  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [again, setAgain] = useState('')

  const [errors, setErrors] = useState<Errors>({})
  const [notice, setNotice] = useState<string | null>(null)
  const [changed, setChanged] = useState(false)
  const [busy, setBusy] = useState(false)
  // The current password is counted like a sign-in: wrong ones add to the
  // account's tally, and while the account is locked it is not looked at. A
  // lock is counted down above the button, the way the sign-in page counts it.
  const { lock, setLock, remaining, lockedFor, counting } = useLockClock()

  /**
   * Everything that can be decided without asking the server, reported all at
   * once. The repeated password is checked only here — the server is never
   * told about it.
   */
  function check(): Errors {
    const found: Errors = {}
    if (!current) found.current_password = t('enterCurrentPassword')

    const length = count(next)
    if (!next) found.new_password = t('enterNewPassword')
    else if (length < PASSWORD_MIN || length > PASSWORD_MAX) {
      found.new_password = t('newPasswordLength', { min: PASSWORD_MIN, max: PASSWORD_MAX })
    } else if (next === current) {
      // The server refuses this too. Catching it here means somebody who has
      // not changed anything is told so before their other devices are signed
      // out for nothing.
      found.new_password = t('newSameAsCurrent')
    }

    if (!again) found.again = t('repeatNewPassword')
    else if (again !== next) found.again = t('passwordsDiffer')
    return found
  }

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (busy) return

    const found = check()
    setErrors(found)
    setNotice(null)
    if (Object.keys(found).length > 0) return

    setBusy(true)
    try {
      // `quiet` matters here. A wrong current password is answered 401, and
      // that 401 belongs to this form rather than to the session: left loud,
      // one mistyped password would throw somebody out of the app they are
      // already signed in to.
      await api<void>('/auth/password', {
        method: 'POST',
        // Sent exactly as typed. Trimming would quietly turn a password into
        // one its owner cannot type back.
        body: { current_password: current, new_password: next },
        quiet: true,
      })
      setCurrent('')
      setNext('')
      setAgain('')
      setErrors({})
      setLock(null)
      setChanged(true)
    } catch (err) {
      // A 401 that is not about the password typed here is the session itself
      // having ended, and `quiet` suppressed the handling that would have said
      // so. Signing out puts them back at the login screen, told why when it
      // was a sign-in on another device.
      if (err instanceof ApiError && err.isUnauthenticated && err.code !== 'bad_credentials') {
        await signOut(err)
        return
      }
      // Locked (423): nothing was compared, so no box is marked and nothing
      // typed is thrown away — the same three can be sent once the wait is over.
      const locked = lockOf(err)
      setLock(locked)
      if (locked) return
      const marked = err instanceof ApiError ? serverErrors(err) : {}
      setErrors(marked)
      setNotice(Object.keys(marked).length > 0 ? null : messageOf(err, auth))
    } finally {
      setBusy(false)
    }
  }

  /** The server's refusals, under the field each one is about. */
  function serverErrors(err: ApiError): Errors {
    const out: Errors = {}
    // The wrong current password marks no field of its own — it is about the
    // account rather than the form — so this decides where it belongs.
    if (err.code === 'bad_credentials') {
      out.current_password = t('wrongCurrentPassword')
      return out
    }
    if (err.fields['current_password'] !== undefined) out.current_password = t('checkCurrentPassword')
    if (err.fields['new_password'] !== undefined) {
      // Two things the server turns a new password away for: its length, and
      // its being the password already in force. Which applies is worked out
      // from the length rule both sides hold, not from the server's English.
      const length = count(next)
      out.new_password =
        length < PASSWORD_MIN || length > PASSWORD_MAX
          ? t('newPasswordLength', { min: PASSWORD_MIN, max: PASSWORD_MAX })
          : t('passwordAlreadyInUse')
    }
    return out
  }

  // The confirmation stays up only while the emptied form is untouched: once
  // somebody starts typing again they are doing something new.
  const settled = !current && !next && !again

  return (
    <section className="card" aria-labelledby={id}>
      <div className="card__body">
        <h2 id={id}>{t('passwordTitle')}</h2>

        <section className="notice notice--warn" aria-labelledby={`${id}-warn`}>
          <h2 id={`${id}-warn`}>{t('otherDevicesTitle')}</h2>
          <p className="settings__note">{t('otherDevicesBody')}</p>
        </section>

        <form onSubmit={submit} noValidate>
          <PasswordField
            id={`${id}-current`}
            label={t('currentPasswordLabel')}
            value={current}
            onChange={setCurrent}
            autoComplete="current-password"
            error={errors.current_password}
            disabled={busy}
          />
          <PasswordField
            id={`${id}-new`}
            label={t('newPasswordLabel')}
            value={next}
            onChange={setNext}
            autoComplete="new-password"
            error={errors.new_password}
            hint={t('passwordHint', { min: PASSWORD_MIN })}
            disabled={busy}
          />
          <PasswordField
            id={`${id}-again`}
            label={t('repeatNewPasswordLabel')}
            value={again}
            onChange={setAgain}
            autoComplete="new-password"
            error={errors.again}
            disabled={busy}
          />

          {lock && (
            <div className="auth__alert auth__alert--lock" role="alert">
              {counting ? (
                <p>
                  <span className="visually-hidden">{t('lockedCountdown', { time: clock(lockedFor) })}</span>
                  <span aria-hidden="true">{t('lockedCountdown', { time: clock(remaining) })}</span>
                </p>
              ) : (
                <p>{t('lockedLater')}</p>
              )}
            </div>
          )}
          {!lock && notice && (
            <p className="auth__alert" role="alert">
              {notice}
            </p>
          )}
          {changed && settled && (
            <p className="settings__done" role="status">
              {t('passwordChanged')}
            </p>
          )}

          <button type="submit" className="button" disabled={busy}>
            {busy ? tc('saving') : t('passwordTitle')}
          </button>
        </form>
      </div>
    </section>
  )
}

function count(s: string): number {
  return [...s].length
}
